import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Plus, Search } from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { useAuth } from '../../state/AuthContext';
import { permitApi } from '../../lib/api';
import type { PermitListItem, PermitStatus } from '../../lib/types';
import { ResultBadge, PermitStatusBadge } from '../../components/StatusBadge';
import { Spinner, EmptyState } from '../../components/Misc';
import { formatDate, titleCase } from '../../lib/format';

const STATUS_FILTERS: Array<{ label: string; statuses?: PermitStatus[] }> = [
  { label: 'All' },
  { label: 'Active', statuses: ['ISSUED', 'ACTIVE_FIREWATCH', 'EXTENDED'] },
  { label: 'Needs review', statuses: ['SUBMITTED', 'UNDER_REVIEW'] },
  { label: 'Closed', statuses: ['CLOSED', 'CANCELLED'] },
];

const CREATE_ROLES = ['org_admin', 'safety_officer', 'area_authority', 'performing_authority', 'platform_admin'];

export default function PermitsListPage() {
  const { currentPlant } = usePlant();
  const { user } = useAuth();
  const canCreate = Boolean(user && CREATE_ROLES.includes(user.role));
  const [permits, setPermits] = useState<PermitListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterIdx, setFilterIdx] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!currentPlant) return;
    let cancelled = false;
    setLoading(true);
    permitApi
      .list(currentPlant.id)
      .then((res) => !cancelled && setPermits(res.permits))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [currentPlant?.id]);

  if (!currentPlant) return <EmptyState icon={<ClipboardList size={32} />} title="No plant selected" />;
  if (loading) return <Spinner label="Loading permits…" />;

  const filter = STATUS_FILTERS[filterIdx]!;
  const filtered = (permits ?? [])
    .filter((p) => !filter.statuses || filter.statuses.includes(p.status))
    .filter((p) => !search || p.permitNumber.toLowerCase().includes(search.toLowerCase()) || p.scopeText.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="stack gap-5">
      <div className="row">
        <div className="stack gap-1">
          <h1 className="h2-section">Permits</h1>
          <p className="body-text">{currentPlant.name} · {currentPlant.code}</p>
        </div>
        <div className="spacer" />
        {canCreate && <Link to="/app/permits/new" className="btn btn-primary"><Plus size={16} /> New Permit</Link>}
      </div>

      <div className="row gap-3" style={{ flexWrap: 'wrap' }}>
        <div className="tabs">
          {STATUS_FILTERS.map((f, i) => (
            <button key={f.label} className={`tab${i === filterIdx ? ' active' : ''}`} onClick={() => setFilterIdx(i)} style={{ background: 'none' }}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <div className="field" style={{ position: 'relative', minWidth: 240 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-tertiary)' }} />
          <input placeholder="Search permit # or scope…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 36 }} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={<ClipboardList size={32} />} title="No permits match" description="Try a different filter or create a new permit." action={canCreate ? <Link to="/app/permits/new" className="btn btn-primary btn-sm">New Permit</Link> : undefined} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Permit</th>
                <th>Type</th>
                <th>Status</th>
                <th>Result</th>
                <th>Contractor</th>
                <th>Planned window</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => (window.location.href = `/app/permits/${p.id}`)}>
                  <td data-label="Permit"><Link to={`/app/permits/${p.id}`} className="mono" style={{ fontWeight: 600, color: 'var(--text)' }}>{p.permitNumber}</Link></td>
                  <td data-label="Type">{titleCase(p.permitType)}</td>
                  <td data-label="Status"><PermitStatusBadge status={p.status} /></td>
                  <td data-label="Result"><ResultBadge result={p.latestOverallResult} /></td>
                  <td data-label="Contractor">{p.performingCompany ?? '—'}</td>
                  <td data-label="Planned window">{formatDate(p.plannedStart)} – {formatDate(p.plannedEnd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
